import { dataDoDiaOperacional } from './dia-operacional';

// Formatação de horário para as telas (pt-BR, no fuso da loja).

const formatadores = new Map<string, Intl.DateTimeFormat>();

function formatador(chave: string, opcoes: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  let f = formatadores.get(chave);
  if (!f) {
    f = new Intl.DateTimeFormat('pt-BR', opcoes);
    formatadores.set(chave, f);
  }
  return f;
}

/** "14:32" */
export function formatarHora(iso: string, fuso = 'America/Sao_Paulo'): string {
  return formatador(`hora:${fuso}`, { timeZone: fuso, hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
}

/** "17/10/2026" */
export function formatarData(iso: string, fuso = 'America/Sao_Paulo'): string {
  return formatador(`data:${fuso}`, {
    timeZone: fuso,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

/** "17/10/2026 14:32" */
export function formatarDataHora(iso: string, fuso = 'America/Sao_Paulo'): string {
  return `${formatarData(iso, fuso)} ${formatarHora(iso, fuso)}`;
}

/** "agora", "há 5 min", "há 2 h", "há 3 dias". */
export function tempoDecorrido(iso: string, agora: Date = new Date()): string {
  const minutos = Math.floor((agora.getTime() - new Date(iso).getTime()) / 60_000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.floor(horas / 24);
  return dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
}

/** Minutos inteiros entre dois instantes (null se faltar um deles). */
export function minutosEntre(inicio: string | null, fim: string | null): number | null {
  if (!inicio || !fim) return null;
  return Math.round((new Date(fim).getTime() - new Date(inicio).getTime()) / 60_000);
}

const DIAS_DA_SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const MESES_CURTOS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

/** Data do calendário no fuso (vira à meia-noite, ao contrário do dia operacional). */
function dataLocal(instante: Date, fuso: string) {
  return dataDoDiaOperacional(instante, 0, fuso);
}

function numeroDoDia(d: { ano: number; mes: number; dia: number }): number {
  return Math.round(Date.UTC(d.ano, d.mes - 1, d.dia) / 86_400_000);
}

/** "terça, 21 out" */
export function formatarDiaDaSemana(instante: Date, fuso = 'America/Sao_Paulo'): string {
  const d = dataLocal(instante, fuso);
  const semana = DIAS_DA_SEMANA[new Date(Date.UTC(d.ano, d.mes - 1, d.dia)).getUTCDay()];
  return `${semana}, ${d.dia} ${MESES_CURTOS[d.mes - 1]}`;
}

/** "hoje, 15:32" · "ontem, 11:20" · "14 out, 11:20" · "14 out 2025, 11:20" (outro ano). */
export function formatarQuando(
  iso: string,
  fuso = 'America/Sao_Paulo',
  agora = new Date(),
): string {
  const d = dataLocal(new Date(iso), fuso);
  const hoje = dataLocal(agora, fuso);
  const hora = formatarHora(iso, fuso);
  const dias = numeroDoDia(hoje) - numeroDoDia(d);
  if (dias === 0) return `hoje, ${hora}`;
  if (dias === 1) return `ontem, ${hora}`;
  return `${formatarDiaMes(iso, fuso, agora)}, ${hora}`;
}

/** "10 out" · "10 out 2025" (outro ano). */
export function formatarDiaMes(
  iso: string,
  fuso = 'America/Sao_Paulo',
  agora = new Date(),
): string {
  const d = dataLocal(new Date(iso), fuso);
  const ano = d.ano === dataLocal(agora, fuso).ano ? '' : ` ${d.ano}`;
  return `${d.dia} ${MESES_CURTOS[d.mes - 1]}${ano}`;
}

/** "jun" (este ano) · "jun 2025": para "cliente desde jun". */
export function formatarMesDoAno(
  iso: string,
  fuso = 'America/Sao_Paulo',
  agora = new Date(),
): string {
  const d = dataLocal(new Date(iso), fuso);
  const mes = MESES_CURTOS[d.mes - 1] ?? '';
  return d.ano === dataLocal(agora, fuso).ano ? mes : `${mes} ${d.ano}`;
}

/** "2026-10-20" → "segunda, 20 out": um dia já resolvido (sem fuso no meio), como o banco guarda. */
export function formatarDataDoDia(dia: string): string {
  const [ano, mes, d] = dia.split('-').map(Number);
  const data = new Date(Date.UTC(ano ?? 1970, (mes ?? 1) - 1, d ?? 1));
  const semana = DIAS_DA_SEMANA[data.getUTCDay()];
  return `${semana}, ${data.getUTCDate()} ${MESES_CURTOS[data.getUTCMonth()]}`;
}

const DIAS_COM_MAIUSCULA = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const DIAS_CURTOS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const doisDigitos = (n: number) => String(n).padStart(2, '0');

/**
 * "Quinta, 22/10": cabeçalho da lista do dia. Para o dia operacional (antes da virada ainda é ontem),
 * passe o início da janela do dia (`janelaDoDiaOperacional(...).inicio`).
 */
export function formatarDiaDaLista(agora: Date, fuso: string): string {
  const d = dataLocal(agora, fuso);
  const semana = DIAS_COM_MAIUSCULA[new Date(Date.UTC(d.ano, d.mes - 1, d.dia)).getUTCDay()];
  return `${semana}, ${doisDigitos(d.dia)}/${doisDigitos(d.mes)}`;
}

/**
 * Rótulo do pedido achado na busca, pelo dia operacional dele (`dia` e `hoje` em "AAAA-MM-DD"):
 * "Hoje 14:40" · "Ontem 10:34" · "Sex 09/10" · "Sex 09/10/2025" (outro ano).
 */
export function rotuloDoDia(dia: string, hoje: string, criadoEm: string, fuso: string): string {
  const [ano, mes, d] = dia.split('-').map(Number);
  const [anoHoje, mesHoje, diaHoje] = hoje.split('-').map(Number);
  const data = { ano: ano ?? 1970, mes: mes ?? 1, dia: d ?? 1 };
  const dias =
    numeroDoDia({ ano: anoHoje ?? 1970, mes: mesHoje ?? 1, dia: diaHoje ?? 1 }) - numeroDoDia(data);
  if (dias === 0) return `Hoje ${formatarHora(criadoEm, fuso)}`;
  if (dias === 1) return `Ontem ${formatarHora(criadoEm, fuso)}`;
  const semana = DIAS_CURTOS[new Date(Date.UTC(data.ano, data.mes - 1, data.dia)).getUTCDay()];
  const anoDiferente = data.ano === (anoHoje ?? data.ano) ? '' : `/${data.ano}`;
  return `${semana} ${doisDigitos(data.dia)}/${doisDigitos(data.mes)}${anoDiferente}`;
}

/** "agora" · "há 20 s" · "há 3 min" · "há 2 h": para o que chega ao vivo (GPS), com segundos. */
export function haQuanto(instante: string | number, agora: number = Date.now()): string {
  const ms = typeof instante === 'number' ? instante : new Date(instante).getTime();
  const s = Math.max(0, Math.round((agora - ms) / 1000));
  if (s < 5) return 'agora';
  if (s < 60) return `há ${s} s`;
  const min = Math.floor(s / 60);
  if (min < 60) return `há ${min} min`;
  return `há ${Math.floor(min / 60)} h`;
}

/** Posição do entregador mais velha que isto é "sem sinal": a tela nunca finge que está ao vivo. */
export const SEM_SINAL_APOS_MS = 2 * 60_000;

export function semSinal(atualizadoEm: string, agora: number = Date.now()): boolean {
  return agora - new Date(atualizadoEm).getTime() > SEM_SINAL_APOS_MS;
}
